'use client';

import {
  AlphabeticalGridFilter,
  AlphabeticalMobileFilter,
  ColumnInfoGroup,
  Container,
  EntryAnimation,
  PageContent,
  TagFilter,
  useNavbarHidden
} from '@stereoagency/ui';
import useTranslation from 'next-translate/useTranslation';
import { useId, useRef, useState } from 'react';
import flush from 'just-flush';
import { twMerge } from '@stereoagency/tailwind-config';
import type { profilesQuery } from './profilesQuery';
import { extractNameFromProfileLike, formatProfileName } from '@/utils';
import { ProfileModal } from '@/components/modals';

type Props = {
  profiles: (typeof profilesQuery)[1];
};

export default function ProfileListingSection({ profiles }: Props) {
  const id = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navbarHidden = useNavbarHidden();
  const topOffsetCx = navbarHidden ? 'top-6' : 'top-[7.5rem]';
  const { t } = useTranslation();
  const groupLeaderProfiles = profiles.filter(
    (profile) => profile.isGroupLeader
  );
  const [filter, setFilter] = useState(['all']);
  const [selected, setSelected] = useState<string | null>(null);
  const [profileOpen, setProfileOpen] = useState<
    (typeof profiles)[number] | null
  >(null);

  const relevantProfiles = filter.includes('all')
    ? profiles
    : groupLeaderProfiles;

  const profileSortKeys = relevantProfiles.reduce<Map<string, string>>(
    (acc, profile) => {
      // if both are set - use the raw name for sorting
      const sortKey = extractNameFromProfileLike(profile);
      if (sortKey) {
        acc.set(profile._id, sortKey);
      }
      return acc;
    },
    new Map()
  );
  const sortedProfiles = relevantProfiles.sort((a, b) => {
    const aSortKey = profileSortKeys.get(a._id);
    const bSortKey = profileSortKeys.get(b._id);
    if (aSortKey && bSortKey) {
      return aSortKey.localeCompare(bSortKey);
    }

    return 0;
  });
  const grouped = sortedProfiles.reduce<Map<string, typeof relevantProfiles>>(
    (map, profile) => {
      const sortKey = profileSortKeys.get(profile._id);
      const firstLetter = sortKey ? sortKey.toUpperCase()[0] : null;
      if (profile.name && firstLetter) {
        const group =
          map.get(firstLetter) ??
          (map.set(firstLetter, []) && map.get(firstLetter)!);
        group.push(profile);
      }
      return map;
    },
    new Map()
  );
  const allLetterOptions = Array.from(grouped.keys()).sort();
  const visibleOptions = selected ? [selected] : allLetterOptions;

  const handleMobileSelect = (letter: string) => {
    const group = containerRef.current?.querySelector(
      `[id="profile-listing-section-${id}-${letter}"]`
    );
    if (group) {
      const dpr = window.devicePixelRatio ?? 1;
      const rect = group.getBoundingClientRect();
      const top =
        rect.top +
        (document.documentElement.scrollTop || document.body.scrollTop) -
        dpr * 120;
      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }
  };

  return (
    <Container ref={containerRef} name={ProfileListingSection.name}>
      <EntryAnimation className="mb-16 md:mb-20">
        <TagFilter
          value={filter}
          options={[
            {
              label: t('global:all'),
              value: 'all',
              suffix: `(${profiles.length})`
            },
            {
              label: t('global:group-leaders'),
              value: 'group-leaders',
              suffix: `(${groupLeaderProfiles.length})`
            }
          ]}
          accentColor="--colors-subtle-500"
          labelColor="--colors-fg-base"
          labelHoverColor="--colors-red-700"
          onChange={(value) => {
            setSelected(null);
            setFilter(value);
          }}
        />
      </EntryAnimation>

      <div className="grid gap-6 grid-cols-[1fr_auto] grid-flow-row auto-rows-max md:grid-cols-[auto_1fr] md:gap-10 lg:gap-12">
        <div className="order-last md:order-first">
          <AlphabeticalGridFilter
            resetAction={{
              label: t('global:all'),
              suffix: `(${profiles.length})`
            }}
            include={allLetterOptions}
            value={selected}
            onChange={setSelected}
            className={twMerge(
              'sticky left-0 hidden transition-all md:flex',
              topOffsetCx
            )}
          />
          <AlphabeticalMobileFilter
            include={allLetterOptions}
            onSelect={handleMobileSelect}
            className={twMerge(
              'sticky left-0 flex transition-all md:hidden',
              topOffsetCx
            )}
          />
        </div>
        <PageContent className="my-0 md:my-0 md:col-start-2">
          <ColumnInfoGroup
            sortDirection="vertical"
            entries={visibleOptions.map((letter) => {
              const group = grouped.get(letter) ?? [];
              return {
                id: `profile-listing-section-${id}-${letter}`,
                title: letter,
                entries: flush(
                  group.map((entry) => {
                    if (!entry.name) return null;

                    return {
                      title: formatProfileName(entry),
                      action: {
                        href: `#${entry._id}`,
                        onClick(event) {
                          event.preventDefault();
                          setProfileOpen(entry);
                        }
                      }
                    };
                  })
                )
              };
            })}
          />
        </PageContent>
      </div>

      <ProfileModal
        profile={profileOpen}
        onClose={() => setProfileOpen(null)}
      />
    </Container>
  );
}
